import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { ApiFragment } from '../entities/api-fragment.entity';
import { PaginatedResults } from '../entities/pagination/paginated-results.entity';

/**
 * The largest page `POST /records/search` accepts. Asking for more is rejected outright —
 * `400 EPARAMINV: 'NrOfResults' cannot be greater than 100` — so a lookup that needs more has to page.
 */
export const MAX_RESULTS_PER_SEARCH: number = 100;

/**
 * How much one aggregate lookup will page through before it gives up and says nothing.
 *
 * Ten pages is not a property of the data — it is where "one column on one page of rows" stops being worth
 * more requests. Both callers share it deliberately: a bound derived from what the data is *expected* to hold
 * (say, five representaties per stuk) turns an unusually rich page into an empty column, which is the failure
 * this ceiling exists to avoid rather than cause.
 */
export const MAX_COLLECTED_RESULTS: number = 10 * MAX_RESULTS_PER_SEARCH;

/**
 * Every result of a search, gathered in pages the API will actually accept.
 *
 * For the aggregate columns this backs, a partial answer is worthless: a stuk shown with half its formats, or a
 * dossier shown with half its children, is worse than one shown with none. So the contract is all-or-nothing —
 * `null` means "do not trust this", and there are two ways to get it: the search reports more results than
 * `maxResults`, or a page comes back **short of what it was asked for** while still claiming there is more. The
 * second keeps the request count at `total / MAX_RESULTS_PER_SEARCH`: a search that will not fill a page is not
 * going to hand over the rest one row at a time.
 *
 * @param fetchPage issues one page of the caller's own search.
 * @param maxResults the point past which the caller would rather know nothing than keep paging.
 */
export function collectSearchResults(
    fetchPage: (startIndex: number, nrOfResults: number) => Observable<PaginatedResults<ApiFragment> | null>,
    maxResults: number
): Observable<ApiFragment[] | null> {
    const fetchFrom = (startIndex: number, collected: ApiFragment[]): Observable<ApiFragment[] | null> =>
        fetchPage(startIndex, MAX_RESULTS_PER_SEARCH).pipe(
            switchMap(page => {
                if (!page) {
                    return of(null);
                }

                if (page.paginationInfo.totalNumberOfResults > maxResults) {
                    return of(null);
                }

                const results: ApiFragment[] = [...collected, ...page.results];

                if (results.length >= page.paginationInfo.totalNumberOfResults) {
                    return of(results);
                }

                return page.results.length < MAX_RESULTS_PER_SEARCH ? of(null) : fetchFrom(results.length, results);
            })
        );

    return fetchFrom(0, []);
}
